import React, {useEffect, useState} from 'react';
import {Button, ButtonPriority, StatesButton, StatesButtonStates, TextField, ThreeDotsLoader} from 'wix-ui-tpa/cssVars';
import {classes} from './CodeInput.st.css';

export enum CodesInputDataHooks {
  TextInput = 'CodesInput.TextInput',
  Button = 'CodesInput.Button',
  ThreeDotsLoader = 'CodesInput.ThreeDotsLoader',
}

export interface CodeInputProps {
  dataHook?: string;
  placeholder?: string;
  appliedValue?: string;
  applyText: string;
  removeText: string;
  onApply: (code: string) => void;
  onRemove: () => void;
  error?: string;
  isLoading?: boolean;
  shouldRedesignCouponsSection?: boolean;
}

export const CodeInput = ({
  dataHook,
  placeholder,
  appliedValue,
  applyText,
  removeText,
  onApply,
  onRemove,
  error,
  isLoading,
  shouldRedesignCouponsSection,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
CodeInputProps) => {
  const [value, setValue] = useState(appliedValue ?? '');
  const [isApplied, setIsApplied] = useState(!!appliedValue);
  const [buttonState, setButtonState] = useState(StatesButtonStates.IDLE);
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  useEffect(() => {
    setValue(appliedValue ?? '');
    setIsApplied(!!appliedValue);
  }, [appliedValue]);

  const onButtonClick = () => {
    const isRemove = shouldRedesignCouponsSection ? !!appliedValue : isApplied;

    if (isRemove) {
      onRemove();
      /* istanbul ignore if */
      if (value !== appliedValue) {
        setValue('');
        setIsApplied(false);
      }
    } else {
      setIsApplied(true);
      onApply(value);
    }
  };

  useEffect(
    () => {
      if (!shouldRedesignCouponsSection) {
        return;
      }

      if (isLoading) {
        setButtonState(StatesButtonStates.IN_PROGRESS);
        return;
      }

      inputRef?.focus();
      setButtonState(StatesButtonStates.IDLE);
    },
    /* eslint-disable react-hooks/exhaustive-deps*/ [isLoading]
  );

  return shouldRedesignCouponsSection ? (
    <div className={classes.container} data-hook={dataHook}>
      <TextField
        data-hook={CodesInputDataHooks.TextInput}
        className={classes.textField}
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        successIcon
        success={!!appliedValue && !error && !isLoading}
        disabled={!!appliedValue && !error && !isLoading}
        error={!!error}
        errorMessage={error}
        newErrorMessage
        inputRef={(input: HTMLInputElement) => setInputRef(input)}
      />
      <StatesButton
        className={classes.statesButton}
        data-hook={CodesInputDataHooks.Button}
        idleContent={appliedValue ? removeText : applyText}
        state={buttonState}
        disabled={!value}
        onClick={onButtonClick}
        inProgressContent={
          <ThreeDotsLoader data-hook={CodesInputDataHooks.ThreeDotsLoader} className={classes.threeDotButton} />
        }
        upgrade={true}
      />
    </div>
  ) : (
    <div data-hook={dataHook}>
      <TextField
        data-hook={CodesInputDataHooks.TextInput}
        className={classes.textFieldOld}
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onClear={() => setValue('')}
        successIcon
        withClearButton={!isApplied}
        disabled={isApplied}
        success={isApplied && !error}
        error={isApplied && !!error}
        errorMessage={error}
        newErrorMessage
      />
      <Button
        className={classes.statesButtonOld}
        data-hook={CodesInputDataHooks.Button}
        upgrade
        fullWidth
        priority={ButtonPriority.basic}
        disabled={!value}
        onClick={onButtonClick}>
        {isApplied ? removeText : applyText}
      </Button>
    </div>
  );
};
